/**
 * dispatchOverview — pure roll-up layer behind the HyperCoder view
 * (Dev ▸ HyperCoder).
 *
 * HyperCoder is the governed coding-agent dispatch surface: a ticket
 * becomes a scoped DispatchContract, a worker returns a reviewable file
 * patch, and the approved dispatch is tracked through the pipeline
 * persisted in `db/devDispatch.ts`:
 *
 *   proposed → dispatched → pr_open → merged   (failed is the off-ramp)
 *
 * This module turns those raw DevDispatch records into what the view
 * renders: stage metadata for the pipeline strip, per-state counts, a
 * newest-first feed, one honest status line per record, and an HONEST
 * GitHub-loop readout — the in-app loop proposes and reviews patches;
 * PRs only open on GitHub once the operator wires a token into the
 * backend (src/lib/integrations/github.ts is the ready client).
 *
 * Pure + DOM-free so it unit-tests under the default node environment.
 */

import type { DevDispatch, DevDispatchState } from '../db/types';

// ---------------------------------------------------------------------------
// Stage metadata — the pipeline strip's single source of truth.
// ---------------------------------------------------------------------------

export interface DispatchStageMeta {
  state: DevDispatchState;
  label: string;
  description: string;
  /** True when nothing follows this state. */
  terminal: boolean;
}

/** The happy path, in order. `failed` is deliberately NOT here — it is the
 *  off-ramp from any non-terminal stage, rendered separately. */
export const DISPATCH_STAGES: readonly DispatchStageMeta[] = [
  {
    state: 'proposed',
    label: 'Proposed',
    description: 'Contract sealed — scope, blast radius, and tests named before any code exists.',
    terminal: false,
  },
  {
    state: 'dispatched',
    label: 'Dispatched',
    description: 'Worker patch reviewed hunk-by-hunk and approved in-app with its exact inverse.',
    terminal: false,
  },
  {
    state: 'pr_open',
    label: 'PR open',
    description: 'A GitHub pull request carries the change; CI and review gate the merge.',
    terminal: false,
  },
  {
    state: 'merged',
    label: 'Merged',
    description: 'The change shipped — the linked ticket auto-projects to done.',
    terminal: true,
  },
] as const;

/** The off-ramp stage, kept out of the happy-path strip. */
export const FAILED_STAGE: DispatchStageMeta = {
  state: 'failed',
  label: 'Failed',
  description: 'The dispatch did not complete — scope violation, worker error, or closed PR.',
  terminal: true,
};

// ---------------------------------------------------------------------------
// Summary roll-up
// ---------------------------------------------------------------------------

export interface DispatchSummary {
  total: number;
  /** Per-state counts — every state always present, zero-filled. */
  counts: Record<DevDispatchState, number>;
  /** Dispatches still moving (proposed + dispatched + pr_open). */
  active: number;
  merged: number;
  failed: number;
  /** Records that carry a real PR reference (number or URL). */
  withPr: number;
  /** All records, most recently updated first. */
  recent: DevDispatch[];
}

const EMPTY_COUNTS: Record<DevDispatchState, number> = {
  proposed: 0,
  dispatched: 0,
  pr_open: 0,
  merged: 0,
  failed: 0,
};

/**
 * Roll raw DevDispatch records up into the view's numbers. Recomputed on
 * demand from the subscribed snapshot — nothing stored, nothing faked.
 */
export function summarizeDispatches(records: DevDispatch[]): DispatchSummary {
  const counts: Record<DevDispatchState, number> = { ...EMPTY_COUNTS };
  let withPr = 0;
  for (const d of records) {
    counts[d.state]++;
    if (typeof d.prNumber === 'number' || (d.prUrl ?? '') !== '') withPr++;
  }
  const recent = [...records].sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt),
  );
  return {
    total: records.length,
    counts,
    active: counts.proposed + counts.dispatched + counts.pr_open,
    merged: counts.merged,
    failed: counts.failed,
    withPr,
    recent,
  };
}

// ---------------------------------------------------------------------------
// Per-record status line — one honest sentence per dispatch.
// ---------------------------------------------------------------------------

/**
 * What is true of this dispatch RIGHT NOW, in one sentence. Never claims a
 * PR exists unless the record actually carries one.
 */
export function dispatchStatusLine(d: DevDispatch): string {
  switch (d.state) {
    case 'proposed':
      return 'Proposed — contract sealed, awaiting in-app patch review.';
    case 'dispatched':
      return 'Dispatched — patch approved in-app; a PR opens once GitHub is connected.';
    case 'pr_open':
      return typeof d.prNumber === 'number'
        ? `PR #${d.prNumber} open — awaiting review and merge.`
        : 'PR open — awaiting review and merge.';
    case 'merged':
      return typeof d.prNumber === 'number'
        ? `Merged via PR #${d.prNumber} — the change shipped.`
        : 'Merged — the change shipped.';
    case 'failed':
      return 'Failed — the dispatch did not complete; re-scope and retry.';
  }
}

// ---------------------------------------------------------------------------
// GitHub loop readout — honest, never simulated.
// ---------------------------------------------------------------------------

export interface GithubLoopStatus {
  /** How many dispatches carry a real PR reference. */
  livePrCount: number;
  /** True only when at least one real PR reference exists. */
  hasLivePrs: boolean;
  /** The honest one-liner the view renders verbatim. */
  message: string;
}

/**
 * Infer the GitHub half of the loop from the records themselves: a real PR
 * reference means the backend loop has produced PRs; none means the loop is
 * in-app only — and the message says exactly that instead of pretending.
 */
export function githubLoopStatus(records: DevDispatch[]): GithubLoopStatus {
  const livePrCount = records.filter(
    (d) => typeof d.prNumber === 'number' || (d.prUrl ?? '') !== '',
  ).length;
  const hasLivePrs = livePrCount > 0;
  return {
    livePrCount,
    hasLivePrs,
    message: hasLivePrs
      ? `${livePrCount} dispatch${livePrCount === 1 ? '' : 'es'} carr${livePrCount === 1 ? 'ies' : 'y'} a live GitHub PR reference.`
      : 'Dispatch proposes and reviews patches in-app; PRs open on GitHub once the operator connects a repo token to the backend.',
  };
}
